import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Divider, LinearProgress
} from '@mui/material';
import {
  Refresh, Person, Article, Category, Event, Flag, Favorite,
  Comment, PersonAdd, Chat, FamilyRestroom, TrendingUp, LocationCity
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const SanataniDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, postsRes, usersRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/admin/posts`, { params: { limit: 5 }, headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/admin/users`, { params: { limit: 5 }, headers: getAuthHeaders() })
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (postsRes.data.success) {
        setRecentPosts(postsRes.data.data.posts || postsRes.data.data || []);
      }
      if (usersRes.data.success) {
        setRecentUsers(usersRes.data.data.users || usersRes.data.data || []);
      }
    } catch (err) {
      setError('Error fetching dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Map nested backend response to stat cards
  const statCards = [
    { label: 'Total Users', value: stats?.users?.total || 0, sub: `Today: ${stats?.users?.today || 0} | Week: ${stats?.users?.thisWeek || 0}`, color: '#FF9800', icon: <Person sx={{ fontSize: 40 }} />, link: '/sanatani-manch/users' },
    { label: 'Active Users', value: stats?.users?.active || 0, sub: '', color: '#4CAF50', icon: <PersonAdd sx={{ fontSize: 40 }} />, link: '/sanatani-manch/users' },
    { label: 'Total Posts', value: stats?.posts?.total || 0, sub: `Today: ${stats?.posts?.today || 0} | Week: ${stats?.posts?.thisWeek || 0}`, color: '#2196F3', icon: <Article sx={{ fontSize: 40 }} />, link: '/sanatani-manch/posts' },
    { label: 'Active Posts', value: stats?.posts?.active || 0, sub: '', color: '#00BCD4', icon: <Article sx={{ fontSize: 40 }} />, link: '/sanatani-manch/posts' },
    { label: 'Total Likes', value: stats?.engagement?.likes || 0, sub: '', color: '#E91E63', icon: <Favorite sx={{ fontSize: 40 }} /> },
    { label: 'Total Comments', value: stats?.engagement?.comments || 0, sub: '', color: '#9C27B0', icon: <Comment sx={{ fontSize: 40 }} /> },
    { label: 'Total Follows', value: stats?.engagement?.follows || 0, sub: '', color: '#3F51B5', icon: <PersonAdd sx={{ fontSize: 40 }} /> },
    { label: 'Total Events', value: stats?.events?.total || 0, sub: `Pending: ${stats?.events?.pending || 0} | Upcoming: ${stats?.events?.upcoming || 0}`, color: '#009688', icon: <Event sx={{ fontSize: 40 }} />, link: '/sanatani-manch/events' },
    { label: 'Pending Flags', value: stats?.flags?.pending || 0, sub: `Total: ${stats?.flags?.total || 0}`, color: '#F44336', icon: <Flag sx={{ fontSize: 40 }} />, link: '/sanatani-manch/flags' },
    { label: 'Kutumb Members', value: stats?.kutumb?.totalMembers || 0, sub: '', color: '#795548', icon: <FamilyRestroom sx={{ fontSize: 40 }} />, link: '/sanatani-manch/kutumb-members' },
    { label: 'Total Chats', value: stats?.chats?.total || 0, sub: `Messages: ${stats?.chats?.messages || 0}`, color: '#607D8B', icon: <Chat sx={{ fontSize: 40 }} /> },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
          🙏 Sanatani Manch Dashboard
        </Typography>
        <IconButton onClick={fetchData} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                bgcolor: stat.color,
                color: 'white',
                height: '100%',
                cursor: stat.link ? 'pointer' : 'default',
                transition: 'transform 0.2s',
                '&:hover': stat.link ? { transform: 'scale(1.03)' } : {},
              }}
              onClick={() => stat.link && navigate(stat.link)}
            >
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: stat.sub ? 0 : undefined }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                  <Typography variant="body1">{stat.label}</Typography>
                </Box>
                {stat.icon}
              </CardContent>
              {stat.sub && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{stat.sub}</Typography>
                </Box>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Posts by Category */}
        {stats?.postsByCategory && stats.postsByCategory.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#9C27B0' }}>
                <Category sx={{ verticalAlign: 'middle', mr: 1 }} />
                Posts by Category
              </Typography>
              {stats.postsByCategory.map((cat, i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{cat.name || 'Unknown'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{cat.count}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((cat.count / (stats.postsByCategory[0]?.count || 1)) * 100, 100)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>
        )}

        {/* Top Cities */}
        {stats?.topCities && stats.topCities.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2196F3' }}>
                <LocationCity sx={{ verticalAlign: 'middle', mr: 1 }} />
                Top Cities
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>#</b></TableCell>
                      <TableCell><b>City</b></TableCell>
                      <TableCell align="right"><b>Users</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topCities.map((city, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{city._id || 'Unknown'}</TableCell>
                        <TableCell align="right">{city.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* User Growth */}
      {stats?.userGrowth && stats.userGrowth.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#4CAF50' }}>
            <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
            User Growth (Last 30 Days)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 120, overflowX: 'auto' }}>
            {stats.userGrowth.map((day, i) => {
              const maxCount = Math.max(...stats.userGrowth.map(d => d.count));
              const heightPct = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
              return (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24 }}>
                  <Typography variant="caption" sx={{ fontSize: 10 }}>{day.count}</Typography>
                  <Box sx={{
                    width: 20,
                    height: `${Math.max(heightPct, 5)}%`,
                    bgcolor: '#4CAF50',
                    borderRadius: '4px 4px 0 0',
                    minHeight: 4,
                  }} />
                  <Typography variant="caption" sx={{ fontSize: 8, transform: 'rotate(-45deg)', mt: 0.5 }}>
                    {day._id?.substring(5) || ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Recent Posts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#FF9800' }}>
              Recent Posts
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Content</b></TableCell>
                    <TableCell><b>Author</b></TableCell>
                    <TableCell><b>Type</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentPosts.slice(0, 5).map((post) => (
                    <TableRow key={post._id} hover>
                      <TableCell>{post.content?.substring(0, 40) || 'N/A'}...</TableCell>
                      <TableCell>{post.profileId?.name || 'Anonymous'}</TableCell>
                      <TableCell>
                        <Chip label={post.postType || 'text'} size="small" color="primary" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentPosts.length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center">No posts yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Recent Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#FF9800' }}>
              Recent Users
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Name</b></TableCell>
                    <TableCell><b>City</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentUsers.slice(0, 5).map((user) => (
                    <TableRow key={user._id} hover>
                      <TableCell>{user.name || 'N/A'}</TableCell>
                      <TableCell>{user.city || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.isBanned ? 'Banned' : 'Active'}
                          size="small"
                          color={user.isBanned ? 'error' : 'success'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentUsers.length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center">No users yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top States */}
        {stats?.topStates && stats.topStates.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#FF9800' }}>
                Top States
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>#</b></TableCell>
                      <TableCell><b>State</b></TableCell>
                      <TableCell align="right"><b>Users</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topStates.map((st, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{st._id || 'Unknown'}</TableCell>
                        <TableCell align="right">{st.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SanataniDashboard;
